// AdminHub.js
//
// Top-level admin landing page introduced in Build 25. Replaces the
// duplicated DefectTrackerAdminView + FeatureRequestsAdminView with a
// single component reachable from the dashboard at /admin.
//
// The old in-app admin views still exist and still work (they continue
// to be state-toggled inside DefectTrackerApp and FeatureRequestsApp),
// but they now redirect to /admin and navigate within it. This keeps
// existing flows working while consolidating the admin UI.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminHub.css';

function AdminHub({ user, userProfile }) {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Admin - Wixon & Cross Desk Suite';
  }, []);

  // Defense in depth: the route is also guarded in Suite.js, but if a
  // non-admin somehow lands here, bounce them.
  if (userProfile?.role !== 'Admin') {
    return (
      <div className="admin-hub">
        <div className="admin-hub-denied">
          <h1>Access Denied</h1>
          <p>This page is only available to administrators.</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-hub">
      <header className="admin-hub-header">
        <div className="admin-hub-header-content">
          <div className="breadcrumb-nav-app">
            <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
              🖥️ Desktop
            </button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
              📦 Dashboard
            </button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item breadcrumb-current">⚙️ Admin</span>
          </div>
          <div className="admin-hub-user-info">
            <span className="user-email">{user?.email}</span>
            <span className="user-role-badge">Admin</span>
          </div>
        </div>
      </header>

      <main className="admin-hub-main">
        <h1 className="admin-hub-title">Admin Panel</h1>
        <p className="admin-hub-subtitle">
          Manage users, applications, and per-user suite access
        </p>

        <div className="admin-cards-grid">
          <div className="admin-card" onClick={() => navigate('/admin/users')}>
            <div className="admin-card-icon">👥</div>
            <h3>Manage Users</h3>
            <p>Assign roles, control access, manage permissions, and reset passwords</p>
            <button className="btn btn-primary">Open</button>
          </div>

          <div className="admin-card" onClick={() => navigate('/admin/apps')}>
            <div className="admin-card-icon">📱</div>
            <h3>Manage Applications</h3>
            <p>Add, edit, and organize the product applications users can file defects against</p>
            <button className="btn btn-primary">Open</button>
          </div>

          <div className="admin-card" onClick={() => navigate('/admin/org-settings')}>
            <div className="admin-card-icon">🏢</div>
            <h3>Organization Settings</h3>
            <p>Control which suite apps your organization uses — affects every user including admins</p>
            <button className="btn btn-primary">Open</button>
          </div>

          <div className="admin-card admin-card--disabled">
            <div className="admin-card-icon">📊</div>
            <h3>Reports & Analytics</h3>
            <p>View defect trends, user activity, and performance metrics</p>
            <span className="coming-soon-badge">Coming Soon</span>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminHub;
